import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';
import axiosClient from '../../../../shared/axios/axios-client';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { AuthService } from '../../../services/auth/auth.service';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';

@Component({
  selector: 'app-usuarios-cadastrados-view',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getPaginatorIntl,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './usuarios-cadastrados-view.html',
  styleUrl: './usuarios-cadastrados-view.scss',
})
export class UsuariosCadastradosView implements OnInit, AfterViewInit {
  /**
   * ESSAS SÃO AS CONFIGURAÇÕES PADRÃO PARA TABELA
   */
  datasource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [
    'nome',
    'empresa',
    'perfil_de_acesso',
    'status',
  ];
  pageSize = 10;
  pageSizeOptions = [5, 10, 25];
  total = 0;
  inicio = 1;
  fim = 10;
  textoContagem = 'Mostrando 0 a 0 de 0 resultados';
  canAddUser = signal(false);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly authService: AuthService
  ) {}

  async ngOnInit() {
    clearPaginas();
    pushPaginaAtiva({
      nome: 'Home',
      rota: '/home',
    });
    pushPaginaAtiva({
      nome: 'Usuários',
      rota: '',
    });
    this.configurarFiltroPersonalizado();
    await this.buscaDadosParaTabela();

    this.canAddUser.set(
      this.authService.isMaster() || this.authService.isGestor()
    );
  }

  ngAfterViewInit() {
    this.datasource.paginator = this.paginator;
    this.datasource.sort = this.sort;
  }

  addAction() {
    this.router.navigate(['/usuarios/cadastrar']);
  }

  async buscaDadosParaTabela() {
    try {
      const res = await axiosClient.get('/usuarios');
      this.datasource.data = res.data;

      this.total = this.datasource.data.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (err: any) {
      this.snackbarService.error(err);
    }
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number, totalRows?: number) {
    const totalExibicao = totalRows ?? this.datasource.data.length;
    const maxPageIndex = Math.max(0, Math.ceil(totalExibicao / pageSize) - 1);
    pageIndex = Math.min(pageIndex, maxPageIndex);

    const startItem = pageIndex * pageSize + 1;
    const endItem = Math.min((pageIndex + 1) * pageSize, totalExibicao);

    this.inicio = totalExibicao > 0 ? startItem : 0;
    this.fim = endItem;
    this.textoContagem = `Mostrando ${this.inicio} a ${
      this.fim
    } de ${totalExibicao} ${totalExibicao === 1 ? 'resultado' : 'resultados'}`;
  }

  /**
   * Até aqui foram só configs padrão
   * daqui para baixo é específico do componente
   */

  filtroNome = '';
  filtroTipoPerfilOptions = [
    {
      key: 1,
      label: 'Master',
      value: 'MASTER',
    },
    {
      key: 2,
      label: 'Gestor',
      value: 'GESTOR',
    },
    {
      key: 3,
      label: 'Saúde',
      value: 'SAUDE',
    },
  ];
  filtroTipoPerfil = '';

  private configurarFiltroPersonalizado() {
    this.datasource.filterPredicate = (data: any, filter: string) => {
      try {
        const filtro = JSON.parse(filter);
        const textoBusca = (filtro.textoBusca || '').toLowerCase().trim();
        const perfilSelecionado = (filtro.perfil || '').toUpperCase();

        // Filtro por texto (nome ou empresa)
        const passaFiltroTexto =
          !textoBusca ||
          data.nome?.toLowerCase().includes(textoBusca) ||
          data.empresaResumo?.nomeFantasia?.toLowerCase().includes(textoBusca);

        // Filtro por perfil de acesso
        const passaFiltroPerfil =
          !perfilSelecionado || (data.role && data.role === perfilSelecionado);

        return passaFiltroTexto && passaFiltroPerfil;
      } catch (e) {
        return true;
      }
    };
  }

  aplicarFiltro() {
    const filtro = {
      textoBusca: this.filtroNome.trim(),
      perfil: this.filtroTipoPerfil,
    };

    this.datasource.filter = JSON.stringify(filtro);

    if (this.datasource.paginator) {
      this.datasource.paginator.firstPage();
    }

    this.atualizarContagem(
      0,
      this.pageSize,
      this.datasource.filteredData.length
    );
  }

  getPerfisNomes(row: any): string {
    if (!row?.role?.length) {
      return '-';
    }

    return this.resolvePerfilNome(row.role);
  }

  resolvePerfilNome(rolename: string): string {
    const res =
      this.filtroTipoPerfilOptions.filter((item) => {
        return item.value.toUpperCase() === rolename.toUpperCase();
      })[0]?.label ?? '-';

    return res;
  }

  getStatusClass(status: number): string {
    return status === 1 ? 'status-active' : 'status-inactive';
  }

  getStatusText(status: number): string {
    return status === 1 ? 'Ativo' : 'Inativo'; // Ou o texto que desejar
  }

  getStatusKind(row: any) {
    if (row.status.toUpperCase() === 'ACTIVE') {
      return 1;
    } else {
      return 0;
    }
  }

  abreTelaParaAtualizacao(row: any) {
    try {
      this.router.navigate([`/usuarios/${row.id}`]);
    } catch (err: any) {
      //TODO: Adicionar handler
    }
  }
}
